import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const apiBase = `${API}/api`;

/**
 * SequenceOverviewTab — per profile een lijst van alle e-mailadressen die een
 * mail ontvingen, in welke step (1-6) ze zitten, en hun reply-status:
 *   🟢 = echte interactie (positive_intent / question / objection_price)
 *   ⚪ = out-of-office / afwezigheid
 *   🔴 = unsubscribe / bounce
 *   ·  = nog geen reply
 *
 * Filters: per step (1-6), alleen-met-groen toggle, search op email/naam/bedrijf.
 * Auto-refresht elke 60s.
 */
const STEPS = [1, 2, 3, 4, 5, 6];

const DOT_LABELS = {
  green: '🟢 Interactie',
  grey: '⚪ Afwezigheid',
  red: '🔴 Stop / bounce',
};

export default function SequenceOverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepFilter, setStepFilter] = useState(null);
  const [onlyGreen, setOnlyGreen] = useState(false);
  const [search, setSearch] = useState('');
  // Re-engagement modal
  const [reengageEmail, setReengageEmail] = useState(null);
  const [reengageData, setReengageData] = useState(null);
  const [reengageLoading, setReengageLoading] = useState(false);
  const [reengageEdit, setReengageEdit] = useState({ subject: '', body_text: '' });
  const [reengageSending, setReengageSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (stepFilter) params.set('step', stepFilter);
    if (onlyGreen) params.set('only_green', 'true');
    try {
      const r = await fetch(`${apiBase}/email/sequence-overview?${params}`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setData(d);
      else toast.error(d.detail || 'Laden mislukt');
    } catch (e) {
      toast.error(e.message || 'Laden mislukt');
    }
    setLoading(false);
  }, [stepFilter, onlyGreen]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = (data?.items || []).filter((i) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (i.email || '').toLowerCase().includes(q) ||
      (i.name || '').toLowerCase().includes(q) ||
      (i.company || '').toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return '–';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
             ' ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const daysSince = (iso) => {
    if (!iso) return 0;
    try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); }
    catch { return 0; }
  };

  const openReengage = async (row) => {
    setReengageEmail(row.email);
    setReengageData(null);
    setReengageLoading(true);
    try {
      const r = await fetch(`${apiBase}/email/reengagement/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: row.email }),
      });
      const d = await r.json();
      if (d.ok) {
        setReengageData(d);
        setReengageEdit({ subject: d.subject, body_text: d.body_text });
      } else {
        const reasonLabels = {
          cooldown: 'Recent al een re-engagement verzonden (cooldown 14 dagen)',
          no_prior_send: 'Dit contact heeft nog geen reguliere mail ontvangen',
          already_engaged: 'Contact reageerde al positief — gebruik handmatige follow-up',
          unsubscribed: 'Contact heeft uitgeschreven',
          bounced: 'Adres bouncet — niet versturen',
          too_recent: `Te recent (${d.days_inactive} dagen geleden, minimaal ${d.needed} dagen nodig)`,
          llm_timeout: 'AI generatie timed out — probeer opnieuw',
          no_llm_key: 'LLM key niet geconfigureerd',
        };
        toast.error(reasonLabels[d.reason] || d.reason || 'Niet eligible');
        setReengageEmail(null);
      }
    } catch (e) {
      toast.error(e.message || 'Laden mislukt');
      setReengageEmail(null);
    }
    setReengageLoading(false);
  };

  const sendReengage = async () => {
    setReengageSending(true);
    try {
      const r = await fetch(`${apiBase}/email/reengagement/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: reengageEmail,
          subject: reengageEdit.subject,
          body_text: reengageEdit.body_text,
          rationale: reengageData?.rationale || '',
        }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(`Re-engagement verzonden naar ${reengageEmail}`);
        setReengageEmail(null);
        load();
      } else {
        toast.error(d.error || 'Verzenden mislukt');
      }
    } catch (e) { toast.error(e.message); }
    setReengageSending(false);
  };

  return (
    <div className="space-y-4" data-testid="sequence-overview-tab">
      {/* Step distribution */}
      <div className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-heading text-sm font-semibold">📊 Verdeling over stappen</h3>
            <p className="text-[11px] text-zinc-500">
              {data?.total ?? 0} contacten in beweging · {data?.green_count ?? 0} met interactie
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-700 cursor-pointer">
              <input
                type="checkbox" checked={onlyGreen}
                onChange={(e) => setOnlyGreen(e.target.checked)}
                className="accent-emerald-600" data-testid="only-green-toggle"
              />
              Alleen met 🟢 interactie
            </label>
            <button onClick={load} className="text-[11px] text-sky-700 hover:text-sky-900 underline" data-testid="refresh-overview-btn">
              ververs
            </button>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {STEPS.map((s) => {
            const count = data?.step_counts?.[String(s)] || 0;
            const active = stepFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStepFilter(active ? null : s)}
                className={`px-2 py-3 rounded text-center transition-colors ${
                  active ? 'bg-violet-600 text-white' :
                  count > 0 ? 'bg-violet-50 hover:bg-violet-100 text-violet-900 border border-violet-200' :
                  'bg-zinc-50 text-zinc-400 border border-zinc-200'
                }`}
                data-testid={`step-filter-${s}`}
                title={active ? 'Klik om filter te resetten' : `Toon alleen contacten in stap ${s}`}
              >
                <div className="font-data font-bold text-2xl">{count}</div>
                <div className="text-[10px] uppercase tracking-wide">Stap {s}</div>
              </button>
            );
          })}
        </div>
        {stepFilter && (
          <div className="mt-2 text-[11px]">
            <span className="text-zinc-500">Actief filter:</span>
            <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-800 rounded-full">
              Stap {stepFilter}
              <button onClick={() => setStepFilter(null)} className="text-violet-600 hover:text-violet-900 text-sm leading-none" data-testid="clear-step-filter">×</button>
            </span>
          </div>
        )}
      </div>

      {/* Search + table */}
      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="px-4 py-2 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
          <Input
            type="text" placeholder="🔍 Zoek op email, naam of bedrijf..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="text-sm max-w-md" data-testid="overview-search-input"
          />
          <span className="text-[11px] text-zinc-500">
            {filtered.length} van {data?.items?.length || 0} contacten
          </span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic">Laden...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic">
            {(data?.items?.length || 0) === 0
              ? 'Nog geen verzonden e-mails in dit profiel'
              : 'Geen contacten matchen de huidige filters'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="overview-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-zinc-500 border-b border-zinc-100">
                  <th className="text-left px-3 py-2 w-8">🔵</th>
                  <th className="text-left px-3 py-2">E-mail</th>
                  <th className="text-left px-3 py-2">Naam / Bedrijf</th>
                  <th className="text-center px-3 py-2">Stap</th>
                  <th className="text-right px-3 py-2">Verzonden</th>
                  <th className="text-right px-3 py-2">Laatste send</th>
                  <th className="text-center px-3 py-2 w-12">🔁</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.email} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`overview-row-${row.email}`}>
                    <td className="px-3 py-2">
                      {row.dot_color === 'green' && <span title={`${DOT_LABELS.green}${row.reply_confidence ? ` (${row.reply_confidence}%)` : ''}`} className="inline-block w-3 h-3 rounded-full bg-emerald-500" data-testid={`dot-${row.email}`} />}
                      {row.dot_color === 'grey' && <span title={DOT_LABELS.grey} className="inline-block w-3 h-3 rounded-full bg-zinc-300" data-testid={`dot-${row.email}`} />}
                      {row.dot_color === 'red' && <span title={DOT_LABELS.red} className="inline-block w-3 h-3 rounded-full bg-rose-500" data-testid={`dot-${row.email}`} />}
                      {!row.dot_color && <span title="Nog geen reply" className="inline-block w-2 h-2 rounded-full bg-zinc-200" />}
                    </td>
                    <td className="px-3 py-2 font-data text-[12px] text-zinc-800">{row.email}</td>
                    <td className="px-3 py-2 text-[12px] text-zinc-700">
                      <div>{row.name || <span className="italic text-zinc-400">–</span>}</div>
                      {row.company && <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{row.company}</div>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${
                        row.current_step === 6 ? 'bg-emerald-100 text-emerald-800' :
                        row.current_step >= 4 ? 'bg-violet-100 text-violet-800' :
                        row.current_step >= 2 ? 'bg-sky-100 text-sky-800' :
                        'bg-zinc-100 text-zinc-700'
                      }`}>{row.current_step}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-[12px] text-zinc-600 font-data">{row.total_sends}×</td>
                    <td className="px-3 py-2 text-right text-[11px] text-zinc-500 font-data">{formatDate(row.last_sent_at)}</td>
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const days = daysSince(row.last_sent_at);
                        const ineligible = row.dot_color === 'red' || row.dot_color === 'green';
                        const tooSoon = days < 30;
                        const disabled = ineligible || tooSoon;
                        return (
                          <button
                            onClick={() => !disabled && openReengage(row)}
                            disabled={disabled}
                            title={
                              ineligible
                                ? (row.dot_color === 'red' ? 'Contact unsubbed/bouncet' : 'Contact reageerde al')
                                : tooSoon
                                ? `Pas eligible na 30 dagen stil (nu ${days})`
                                : `Genereer re-engagement mail (${days} dagen stil)`
                            }
                            className={`text-base leading-none px-1 rounded transition-colors ${
                              disabled
                                ? 'opacity-25 cursor-not-allowed'
                                : 'hover:bg-sky-100 cursor-pointer text-sky-600 hover:text-sky-800'
                            }`}
                            data-testid={`reengage-btn-${row.email}`}
                          >🔁</button>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="text-[10px] text-zinc-500 flex items-center gap-4 flex-wrap pl-2">
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1.5" />Echte interactie</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-zinc-300 mr-1.5" />Out-of-office</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500 mr-1.5" />Unsub / bounce</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-zinc-200 mr-1.5" />Geen reply</span>
      </div>

      {/* Re-engagement modal */}
      {reengageEmail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !reengageSending && setReengageEmail(null)}>
          <div className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="reengage-modal">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h4 className="font-heading text-sm font-semibold flex items-center gap-2">
                  🔁 Re-engagement — {reengageEmail}
                </h4>
                {reengageData?.context && (
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    {reengageData.context.days_inactive} dagen stil ·
                    eerder verzonden: stap {(reengageData.context.previously_sent_steps || []).join(', ')} ·
                    opens: {reengageData.context.previously_opened} ·
                    clicks: {reengageData.context.previously_clicked}
                  </p>
                )}
              </div>
              <button onClick={() => !reengageSending && setReengageEmail(null)} className="text-zinc-400 hover:text-zinc-700 text-2xl leading-none" data-testid="reengage-close">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {reengageLoading ? (
                <div className="text-sm text-zinc-500 italic flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                  AI genereert een gepersonaliseerde re-engagement mail...
                </div>
              ) : reengageData?.ok ? (
                <>
                  {reengageData.rationale && (
                    <div className="bg-sky-50 border border-sky-200 rounded p-2.5 text-[11px] text-sky-900" data-testid="reengage-rationale">
                      <strong>Invalshoek:</strong> {reengageData.rationale}
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wide text-zinc-600 block mb-1">Subject</label>
                    <Input
                      type="text" value={reengageEdit.subject}
                      onChange={(e) => setReengageEdit((s) => ({ ...s, subject: e.target.value }))}
                      maxLength={80} data-testid="reengage-subject-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wide text-zinc-600 block mb-1">Body (NL)</label>
                    <textarea
                      value={reengageEdit.body_text}
                      onChange={(e) => setReengageEdit((s) => ({ ...s, body_text: e.target.value }))}
                      rows={11}
                      className="w-full px-3 py-2 text-sm font-data border border-zinc-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                      data-testid="reengage-body-textarea"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1">
                      Vrij bewerkbaar voor verzenden. Voorvertoning wordt automatisch HTML-genformatteerd.
                    </p>
                  </div>
                </>
              ) : null}
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
              <Button variant="outline" size="sm" onClick={() => setReengageEmail(null)}
                      disabled={reengageSending} data-testid="reengage-cancel">
                Annuleren
              </Button>
              <Button size="sm" onClick={sendReengage}
                      disabled={reengageSending || reengageLoading || !reengageEdit.subject || !reengageEdit.body_text}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid="reengage-send-btn">
                {reengageSending ? 'Versturen...' : '📨 Verstuur re-engagement'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
